<template>
  <section class="hx-pagina" aria-labelledby="expediente-titulo" data-testid="historial-expediente">
    <div class="hx-contenido">
      <!-- Cargando -->
      <div v-if="estado === 'cargando'" class="hx-cargando" aria-busy="true" data-testid="expediente-cargando">
        <h1 id="expediente-titulo" class="hx-sr">Cargando el expediente del auto</h1>
        <div class="hx-tarjeta hx-skel-bloque" aria-hidden="true">
          <span class="hx-skel hx-skel-titulo"></span>
          <span class="hx-skel hx-skel-linea"></span>
          <span class="hx-skel hx-skel-linea corta"></span>
        </div>
        <div class="hx-resumen cuatro" aria-hidden="true">
          <span v-for="n in 4" :key="n" class="hx-tarjeta hx-skel-stat"><span class="hx-skel hx-skel-linea"></span></span>
        </div>
        <div class="hx-tarjeta hx-skel-bloque" aria-hidden="true">
          <span v-for="n in 4" :key="n" class="hx-skel hx-skel-fila"></span>
        </div>
      </div>

      <!-- No existe -->
      <div v-else-if="estado === 'no-existe'" class="hx-tarjeta hx-estado" role="alert" data-testid="expediente-no-existe">
        <AppIcon name="exclamation" size="h-6 w-6" class="hx-estado-icono" />
        <h1 id="expediente-titulo" ref="estadoTitulo" class="hx-estado-titulo" tabindex="-1">Este auto no existe.</h1>
        <p class="hx-texto-suave">Puede que el enlace sea antiguo. Búscalo por su placa, su serie o el nombre del dueño.</p>
        <a class="rm-btn rm-btn-primario" :href="hrefVolver" data-testid="expediente-volver" @click="alClicVolver">Buscar otro auto</a>
      </div>

      <!-- Error -->
      <div v-else-if="estado === 'error'" class="hx-tarjeta hx-estado" role="alert" data-testid="expediente-error">
        <AppIcon name="exclamation" size="h-6 w-6" class="hx-estado-icono" />
        <h1 id="expediente-titulo" ref="estadoTitulo" class="hx-estado-titulo" tabindex="-1">No pudimos cargar el expediente</h1>
        <p class="hx-texto-suave">Revisa la conexión e inténtalo de nuevo.</p>
        <div class="hx-estado-acciones">
          <button type="button" class="rm-btn rm-btn-primario" data-testid="expediente-reintentar" @click="cargar">Reintentar</button>
          <a class="rm-btn rm-btn-secundario" :href="hrefVolver" @click="alClicVolver">Cambiar de auto</a>
        </div>
      </div>

      <template v-else-if="datos">
        <!-- Auto y dueño -->
        <header class="hx-tarjeta hx-auto" data-testid="expediente-auto">
          <div class="hx-auto-datos">
            <h1 id="expediente-titulo" ref="titulo" class="hx-auto-titulo" tabindex="-1">{{ titulo }}</h1>
            <div class="hx-auto-sub">
              <AppPlaca :placa="vehiculo.placa" />
              <span v-if="vehiculo.serie">Serie <span class="hx-mono" data-testid="expediente-serie">{{ vehiculo.serie }}</span></span>
              <span v-if="detalles">{{ detalles }}</span>
              <span v-if="vehiculo.activo === false" class="hx-chip hx-chip-peligro">Dado de baja</span>
            </div>
          </div>
          <div class="hx-auto-acciones">
            <a class="rm-btn rm-btn-secundario" :href="hrefVolver" data-testid="expediente-cambiar" @click="alClicVolver">
              <AppIcon name="search" size="h-4 w-4" />
              Cambiar de auto
            </a>
          </div>
          <div class="hx-dueno" data-testid="expediente-dueno">
            <span v-if="dueno" class="hx-dueno-nombre">
              <b>{{ dueno.nombre }}</b>
              <span v-if="dueno.deBaja" class="hx-chip hx-chip-peligro" data-testid="expediente-dueno-baja">Cliente dado de baja</span>
              <template v-for="(tel, i) in dueno.telefonos">
                · <a :key="'telefono-' + i" class="hx-enlace" :href="'tel:' + tel.digitos">{{ tel.numero }}</a>
              </template>
            </span>
            <span v-else class="hx-texto-suave">Sin dueño registrado</span>
            <span v-if="otrosAutos.length" class="hx-otros">
              <span class="hx-texto-suave">Otros autos:</span>
              <a
                v-for="auto in otrosAutos"
                :key="auto._id"
                class="hx-otro-auto"
                :href="hrefAuto(auto._id)"
                data-testid="expediente-otro-auto"
                @click="alClicAuto($event, auto._id)"
              >{{ auto.texto }}</a>
            </span>
          </div>
        </header>

        <!-- Resumen -->
        <section aria-labelledby="expediente-resumen" data-testid="expediente-resumen">
          <h2 id="expediente-resumen" class="hx-sr">Resumen</h2>
          <ul class="hx-resumen" :class="mostrarGasto ? 'cuatro' : 'tres'" data-testid="resumen-lista">
            <li class="hx-tarjeta hx-stat" data-testid="resumen-ultima">
              <span class="hx-stat-etiqueta">Última visita</span>
              <strong class="hx-stat-valor" data-testid="resumen-ultima-valor">{{ fecha(resumen.ultimaVisita) || 'Sin visitas' }}</strong>
              <span v-if="haceTexto" class="hx-stat-nota">{{ haceTexto }}</span>
            </li>
            <li class="hx-tarjeta hx-stat" data-testid="resumen-km">
              <span class="hx-stat-etiqueta">Último kilometraje</span>
              <span v-if="odometro.length" class="hx-odometro" role="img" :aria-label="kmTexto + ' kilómetros'" data-testid="resumen-odometro">
                <span v-for="(digito, i) in odometro" :key="i" class="hx-odometro-digito">{{ digito }}</span>
                <em>km</em>
              </span>
              <strong v-else class="hx-stat-valor">Sin dato</strong>
              <span class="hx-stat-nota">{{ kmNota }}</span>
            </li>
            <li class="hx-tarjeta hx-stat" data-testid="resumen-visitas">
              <span class="hx-stat-etiqueta">Visitas</span>
              <strong class="hx-stat-valor" data-testid="resumen-visitas-valor">{{ numero(resumen.visitas || 0) }}</strong>
              <span v-if="desdeTexto" class="hx-stat-nota">{{ desdeTexto }}</span>
            </li>
            <li v-if="mostrarGasto" class="hx-tarjeta hx-stat" data-testid="resumen-gasto">
              <span class="hx-stat-etiqueta">Gasto acumulado</span>
              <strong class="hx-stat-valor" data-testid="resumen-gasto-valor">{{ dinero(resumen.gastoTotal) }}</strong>
              <span class="hx-stat-nota">Ventas de sus órdenes · solo superAdmin</span>
            </li>
          </ul>
        </section>

        <!-- Última visita -->
        <div v-if="refacciones.length || notaFinal" class="hx-dos">
          <section v-if="refacciones.length" class="hx-tarjeta" aria-labelledby="expediente-refacciones" data-testid="expediente-refacciones">
            <h2 id="expediente-refacciones" class="hx-tarjeta-titulo">
              Refacciones de la última visita
              <small v-if="ultima.folio">{{ ultima.folio }} · {{ fecha(ultima.fecha) }}</small>
            </h2>
            <ul class="hx-refacciones">
              <li v-for="(refaccion, i) in refacciones" :key="i">
                <span>{{ refaccion.nombre }}</span>
                <span class="hx-refaccion-cantidad">{{ refaccion.cantidad }}</span>
              </li>
            </ul>
          </section>
          <section v-if="notaFinal" class="hx-tarjeta hx-pendiente" aria-labelledby="expediente-nota" data-testid="expediente-nota">
            <h2 id="expediente-nota" class="hx-tarjeta-titulo">Nota final de la última visita</h2>
            <blockquote class="hx-pendiente-texto">{{ notaFinal }}</blockquote>
            <p class="hx-texto-suave">Nota final de la orden <span class="hx-mono">{{ ultima.folio }}</span><template v-if="ultima.fecha"> · {{ fecha(ultima.fecha) }}</template></p>
          </section>
        </div>

        <!-- Visitas -->
        <section class="hx-tarjeta hx-visitas" aria-labelledby="expediente-visitas" data-testid="expediente-visitas">
          <div class="hx-visitas-cabecera">
            <h2 id="expediente-visitas" class="hx-tarjeta-titulo">
              Visitas
              <small>{{ conteoTexto }}</small>
            </h2>
            <div class="hx-filtros" role="group" aria-label="Filtrar visitas">
              <button
                v-for="opcion in filtros"
                :key="opcion.id"
                type="button"
                :aria-pressed="String(filtro === opcion.id)"
                :data-testid="'filtro-' + opcion.id"
                @click="cambiarFiltro(opcion.id)"
              >{{ opcion.texto }}</button>
            </div>
          </div>

          <ol v-if="visibles.length" class="hx-linea" data-testid="expediente-lista">
            <VisitaOrden
              v-for="visita in visibles"
              :key="visita._id"
              :visita="visita"
              :abierta="abiertas.includes(visita._id)"
              @alternar="alternar"
            />
          </ol>
          <p v-else class="hx-vacio-visitas" data-testid="expediente-sin-visitas">{{ vacioTexto }}</p>

          <div v-if="restantes > 0" class="hx-mas">
            <button type="button" class="rm-btn rm-btn-secundario" data-testid="expediente-ver-mas" @click="verTodas = true">
              Ver {{ restantes }} más<template v-if="desdeRestantes">, desde {{ desdeRestantes }}</template>
            </button>
          </div>
        </section>
      </template>
    </div>
  </section>
</template>

<script>
/*
 * Historial › Expediente del vehículo (GET /api/historial/vehiculo/:id).
 * Arriba el auto y su dueño (con sus otros autos), luego el resumen, las
 * refacciones y la nota final de la última visita, y abajo todas las
 * visitas con filtros en el cliente. Cada visita se despliega en su lugar.
 *
 * `ordenId` (?orden=) abre esa visita y la desplaza a la vista.
 * Entrega 1: solo lectura (sin próximo servicio, recordatorios ni PDF).
 */
import Historial, { esNoEncontrado } from '@/services/historial'
import '@/assets/css/historial.css'
import AppIcon from '@/components/ui/AppIcon.vue'
import AppPlaca from '@/components/ui/AppPlaca.vue'
import VisitaOrden from './VisitaOrden.vue'
import {
  formatearDinero,
  formatearFechaNumerica,
  formatearNumero,
  haceDias,
  mesYAnio,
  telefonosDe,
  textoOracion,
} from '@/utils/formato'
import {
  FILTROS_VISITAS,
  descripcionDeAuto,
  detallesDeAuto,
  digitosOdometro,
  esCancelada,
  esClicSimple,
  filtrarVisitas,
  guardarReciente,
  nombreDeAuto,
  nombreLimpio,
} from '@/utils/historial'

export const VISITAS_VISIBLES = 6
export const KM_POR_DIA_MAXIMO = 500

const VACIOS = {
  todas: 'Este auto todavía no tiene órdenes de servicio.',
  anio: 'No vino en el último año.',
  factura: 'Ninguna de sus órdenes tiene factura.',
  canceladas: 'No tiene órdenes canceladas.',
}

const plural = (n, singular, varios) => `${formatearNumero(n, { decimales: 0 })} ${n === 1 ? singular : varios}`

export default {
  name: 'HistorialExpediente',
  components: { AppIcon, AppPlaca, VisitaOrden },
  props: {
    vehiculoId: { type: String, required: true },
    ordenId: { type: String, default: '' },
    volver: { type: Object, default: () => ({ name: 'historial' }) },
  },
  data() {
    return {
      estado: 'cargando',
      datos: null,
      filtro: 'todas',
      verTodas: false,
      abiertas: [],
      filtros: FILTROS_VISITAS,
    }
  },
  computed: {
    vehiculo() {
      return (this.datos && this.datos.vehiculo) || {}
    },
    titulo() {
      return nombreDeAuto(this.vehiculo)
    },
    detalles() {
      return detallesDeAuto(this.vehiculo)
    },
    dueno() {
      const dueno = this.datos && this.datos.dueno
      if (!dueno) return null
      const telefonos = telefonosDe(dueno.telefonos).map((numero) => ({ numero, digitos: numero.replace(/\D/g, '') }))
      return { nombre: nombreLimpio(dueno.nombre) || 'Sin nombre', telefonos, deBaja: dueno.activo === false }
    },
    otrosAutos() {
      return ((this.datos && this.datos.otrosAutos) || [])
        .filter((auto) => auto && auto._id)
        .map((auto) => ({
          _id: auto._id,
          texto: [descripcionDeAuto(auto.descripcion), auto.placa].filter(Boolean).join(' · '),
        }))
    },
    resumen() {
      return (this.datos && this.datos.resumen) || {}
    },
    mostrarGasto() {
      return this.resumen.gastoTotal !== null && this.resumen.gastoTotal !== undefined
    },
    haceTexto() {
      return haceDias(this.resumen.diasDesdeUltima)
    },
    odometro() {
      return Number(this.resumen.ultimoKm) > 0 ? digitosOdometro(this.resumen.ultimoKm) : []
    },
    kmTexto() {
      return formatearNumero(this.resumen.ultimoKm, { decimales: 0 })
    },
    kmNota() {
      // Más de 500 km al día sale de capturas basura del kilometraje: no se muestra
      const porDia = Number(this.resumen.kmPorDia)
      if (porDia > 0 && porDia <= KM_POR_DIA_MAXIMO) return `${formatearNumero(this.resumen.kmPorDia, { decimales: 0 })} km al día, según sus visitas`
      return this.odometro.length ? 'De la visita más reciente que lo registró' : 'Ninguna visita registró kilometraje'
    },
    desdeTexto() {
      const desde = mesYAnio(this.resumen.primeraVisita, { utc: true })
      return desde && Number(this.resumen.visitas) > 0 ? `Desde ${desde}` : ''
    },
    ultima() {
      return (this.datos && this.datos.ultimaVisita) || {}
    },
    refacciones() {
      return (Array.isArray(this.ultima.refacciones) ? this.ultima.refacciones : [])
        .filter(Boolean)
        .map((r) => ({ nombre: textoOracion(r.nombre) || 'Sin nombre', cantidad: formatearNumero(r.cantidad) }))
    },
    notaFinal() {
      return textoOracion(this.ultima.notaFinal)
    },
    visitas() {
      return (this.datos && this.datos.visitas) || []
    },
    conteoTexto() {
      const total = this.visitas.length
      const canceladas = this.visitas.filter(esCancelada).length
      const partes = [plural(total, 'orden', 'órdenes')]
      if (canceladas) partes.push(plural(canceladas, 'cancelada', 'canceladas'))
      return partes.join(' · ')
    },
    filtradas() {
      return filtrarVisitas(this.visitas, this.filtro)
    },
    visibles() {
      return this.verTodas ? this.filtradas : this.filtradas.slice(0, VISITAS_VISIBLES)
    },
    restantes() {
      return this.filtradas.length - this.visibles.length
    },
    desdeRestantes() {
      const ultima = this.filtradas[this.filtradas.length - 1]
      return ultima ? mesYAnio(ultima.recibida, { utc: true }) : ''
    },
    vacioTexto() {
      return VACIOS[this.filtro] || VACIOS.todas
    },
    hrefVolver() {
      return this.$router.resolve(this.volver).href
    },
  },
  watch: {
    vehiculoId() {
      this.cargar()
      try {
        window.scrollTo(0, 0)
      } catch (error) {
        // Sin scroll programático (pruebas): no pasa nada
      }
    },
    ordenId(nuevo) {
      this.irAOrden(nuevo)
    },
  },
  created() {
    this.ultimaCarga = 0
    this.cargar()
  },
  beforeDestroy() {
    this.ultimaCarga++
  },
  methods: {
    async cargar() {
      const carga = ++this.ultimaCarga
      const mismoAuto = this.datos && this.datos.vehiculo && this.datos.vehiculo._id === this.vehiculoId
      this.estado = 'cargando'
      if (!mismoAuto) {
        this.datos = null
        this.filtro = 'todas'
        this.verTodas = false
        this.abiertas = []
      }
      try {
        const datos = await Historial.expediente(this.vehiculoId)
        if (carga !== this.ultimaCarga) return
        this.datos = datos
        this.estado = 'listo'
        this.recordar()
        document.title = `${this.titulo} · Historial - Automotriz RM`
        await this.$nextTick()
        if (this.ordenId && this.visitas.some((v) => v._id === this.ordenId)) this.irAOrden(this.ordenId)
        else this.enfocarTitulo()
      } catch (error) {
        if (carga !== this.ultimaCarga) return
        this.estado = esNoEncontrado(error) ? 'no-existe' : 'error'
        // El enlace que se pulsó ya no existe: el foco pasa al mensaje
        await this.$nextTick()
        const titulo = this.$refs.estadoTitulo
        if (titulo && typeof titulo.focus === 'function') titulo.focus({ preventScroll: true })
      }
    },
    recordar() {
      // Solo datos del auto: nada del dueño (ver utils/historial.js)
      guardarReciente({ _id: this.vehiculo._id || this.vehiculoId, descripcion: this.titulo, placa: this.vehiculo.placa || '' })
    },
    enfocarTitulo() {
      const titulo = this.$refs.titulo
      if (titulo && typeof titulo.focus === 'function') titulo.focus({ preventScroll: true })
    },
    irAOrden(ordenId) {
      if (!ordenId || !this.datos) return
      const indice = this.visitas.findIndex((v) => v._id === ordenId)
      if (indice === -1) return
      this.filtro = 'todas'
      if (indice >= VISITAS_VISIBLES) this.verTodas = true
      if (!this.abiertas.includes(ordenId)) this.abiertas.push(ordenId)
      this.$nextTick(() => {
        const elemento = Array.from(this.$el.querySelectorAll('[data-orden-id]')).find((el) => el.dataset.ordenId === ordenId)
        if (!elemento) return
        const sinMovimiento =
          typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (typeof elemento.scrollIntoView === 'function') {
          elemento.scrollIntoView({ block: 'start', behavior: sinMovimiento ? 'auto' : 'smooth' })
        }
        const boton = elemento.querySelector('button')
        if (boton) boton.focus({ preventScroll: true })
      })
    },
    alternar(id) {
      const i = this.abiertas.indexOf(id)
      if (i === -1) this.abiertas.push(id)
      else this.abiertas.splice(i, 1)
    },
    cambiarFiltro(id) {
      this.filtro = id
      this.verTodas = false
    },
    rutaAuto(id) {
      return { name: 'historialVehiculo', params: { vehiculo: String(id) } }
    },
    hrefAuto(id) {
      return this.$router.resolve(this.rutaAuto(id)).href
    },
    abrirAuto(id) {
      this.$router.push(this.rutaAuto(id)).catch(() => {})
    },
    volverABuscar() {
      this.$router.push(this.volver).catch(() => {})
    },
    // Ctrl/Cmd + clic o botón central: el navegador abre el href en otra pestaña
    alClicAuto(event, id) {
      if (!esClicSimple(event)) return
      event.preventDefault()
      this.abrirAuto(id)
    },
    alClicVolver(event) {
      if (!esClicSimple(event)) return
      event.preventDefault()
      this.volverABuscar()
    },
    fecha(valor) {
      return formatearFechaNumerica(valor, { utc: true })
    },
    dinero(valor) {
      return formatearDinero(valor) || 'Sin dato'
    },
    numero(valor) {
      return formatearNumero(valor, { decimales: 0 })
    },
  },
}
</script>
